/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-call */
import { useEffect, useState } from 'react';
import classNames from 'classnames';
import { useForm, SubmitHandler } from 'react-hook-form';
import { useUserInfo } from '@lib/user-context';
import { useRouter } from 'next/router';
import axios from 'axios';

// --------------------------------------------------------

import { gql } from '@apollo/client';
import { graphql } from '@lib/api';
import { AGENT_BY_ID_QUERY } from '@lib/fragments';

// --------------------------------------------------------

import { toKebabCase, slugify, captchaVerify, getSiteURL } from '@lib/utils';
import { reportError } from '@lib/report-error';
import { RichTextField, ImageField } from '@lib/interfaces';
import { useLeadsPlusAgentData } from '@lib/hooks';
import { getAgentIds as getAgentIdsUtil } from '@lib/agent-utils';

// --------------------------------------------------------

import { Input, Radio, Textarea, Checkbox, Select } from '@components/form/form-components';

import Content from '@components/content';
import Button from '@components/button';
import Image from '@components/image';
import Toast from '@components/toast';

import styles from './styles.module.scss';

const {
  theme_contained,
  theme_office,
  form_wrapper,
  form_bg_img,
  form_container,
  form_inside_container,
  form,
  form_title,
  form_row,
  form_support_block,
  form_image,
  form_support_block_text,
  form_success,
  error_style,
  disclaimer,
  submit_button,
  theme_no_padding
} = styles;

const themeOptions = {
  office: theme_office,
  Contained: theme_contained,
  'no padding': theme_no_padding
};

declare global {
  interface Window {
    Rollbar?: {
      error: (error: unknown) => void;
    };
  }
}

type FieldType = 'Short Text' | 'Radio' | 'Long Text' | 'Checkbox' | 'Dropdown';

interface CMSField {
  type: FieldType;
  name: string;
  label: string;
  value?: string[];
  required?: boolean;
  placeholder?: string;
}

interface FormProps {
  items: CMSField[];
}

export interface DynamicFormProps {
  header?: string;
  formDescription?: RichTextField | string;
  optionalFields?: FormProps;
  theme?: string;
  requestType: string | string[];
  shouldShowConsentToContact: boolean;
  shouldShowWorkingWithAgent: boolean;
  shouldShowComments: boolean;
  shouldAllowLeadsPlusAgent: boolean;
  agentIds: number[];
  officeId: number;
  listingId?: number;
  emailSubject: string;
  extraRecipients: string[];
  recipientNotification: string;
  confirmationToSender: string;
  defaultCommentValue?: string;
  submitLabel?: string;
  backgroundImage?: ImageField;
  supportHeader?: string;
  supportDescription?: RichTextField | string;
  supportImage?: ImageField;
  className?: string;
  nextStepCallback?: (step: number) => void;
  defaultValueWorkingWithAgent?: string;
  contextData?: Record<string, unknown>;
}

const DynamicForm = (props: DynamicFormProps) => {
  const {
    header,
    formDescription,
    theme,
    optionalFields,
    shouldShowComments = true,
    defaultCommentValue,
    submitLabel = 'Submit',
    shouldShowConsentToContact,
    shouldShowWorkingWithAgent,
    defaultValueWorkingWithAgent = '',
    requestType,
    backgroundImage,
    supportHeader,
    supportDescription,
    supportImage,
    extraRecipients,
    recipientNotification,
    confirmationToSender,
    emailSubject,
    officeId,
    listingId,
    agentIds,
    className,
    nextStepCallback,
    shouldAllowLeadsPlusAgent,
    contextData
  } = props;

  const router = useRouter();
  const [submitted, setSubmitted] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [disabled, setDisabled] = useState(false);
  const [workingWithAgentValue, setWorkingWithAgentValue] = useState<string>(
    defaultValueWorkingWithAgent || ''
  );

  // -----------------------------------------------

  const { leadsPlusAgent, setLeadsPlusAgent, getLeadsPlusAgentFromSessionStorage } =
    useLeadsPlusAgentData();

  useEffect(() => {
    const leadsPlusAgentData = getLeadsPlusAgentFromSessionStorage();
    if (leadsPlusAgentData) {
      setLeadsPlusAgent(leadsPlusAgentData);
    }
  }, [router, getLeadsPlusAgentFromSessionStorage, setLeadsPlusAgent]);

  // -----------------------------------------------

  const hasBackgroundImage = backgroundImage?.src ? true : false;

  const classes = classNames(form_wrapper, {
    [theme_contained]: hasBackgroundImage || supportHeader || supportDescription,
    [themeOptions[theme as keyof typeof themeOptions]]:
      themeOptions[theme as keyof typeof themeOptions],
    [className]: className
  });

  const { userInfo } = useUserInfo();
  let fullName = '';
  if (userInfo?.given_name && userInfo?.family_name) {
    fullName = `${userInfo?.given_name} ${userInfo?.family_name}`;
  }

  // -----------------------------------------------

  const { register, handleSubmit, setValue } = useForm({
    shouldUseNativeValidation: true
  });

  useEffect(() => {
    if (fullName) {
      setValue('name', fullName);
    }
  }, [fullName, setValue]);

  useEffect(() => {
    if (userInfo?.email) {
      setValue('email', userInfo.email);
    }
  }, [userInfo?.email, setValue]);

  // -----------------------------------------------

  useEffect(() => {
    if (userInfo?.agent) {
      const fetchData = async () => {
        const variables = {
          agentId: Number(userInfo?.agent)
        };

        const fetchedData = await graphql(
          gql`
            ${AGENT_BY_ID_QUERY}
          `,
          variables
        );
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        setWorkingWithAgentValue(fetchedData?.agentSearch?.items[0]?.displayPrimary ?? '');
        setValue('workingWithAgent', fetchedData?.agentSearch?.items[0]?.displayPrimary ?? '');
      };
      fetchData().catch((error) => {
        console.error('Error fetching agent data:', error);
      });
    } else {
      setWorkingWithAgentValue(defaultValueWorkingWithAgent);
      setValue('workingWithAgent', defaultValueWorkingWithAgent);
    }
  }, [defaultValueWorkingWithAgent, userInfo?.agent]);

  const getAgentIds = () => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    return getAgentIdsUtil(agentIds, shouldAllowLeadsPlusAgent, userInfo, leadsPlusAgent);
  };

  // -----------------------------------------------

  const inputType = (item: CMSField, index: number) => {
    // Ensure unique field name by combining name/label with index if no explicit name provided
    // Remove special characters and sanitize the field name
    const sanitizedLabel = toKebabCase(item.name || item.label).replace(/[^a-z0-9-]/g, '');
    const fieldName = sanitizedLabel;
    const key = `${toKebabCase(item.type)}-${index}`;

    switch (item.type) {
      case 'Short Text':
        return (
          <Input
            key={key}
            label={item.label}
            name={fieldName}
            placeholder={item.placeholder}
            register={register(fieldName, {
              required: item?.required
            })}
            type="text"
            defaultInputValue={defaultCommentValue || ''}
            required={item.required}
          />
        );
      case 'Radio': {
        const radioOptionsArray = item?.value?.map((option: string) => ({
          label: option,
          value: option
        }));
        return (
          <Radio
            key={key}
            label={item.label}
            inputName={fieldName}
            options={radioOptionsArray || []}
            register={register}
            required={item.required}
          />
        );
      }
      case 'Long Text':
        return (
          <Textarea
            key={key}
            label={item.label}
            name={fieldName}
            placeholder={item.placeholder}
            register={register}
            required={item.required}
          />
        );
      case 'Checkbox': {
        const checkboxOptionsArray = item.value?.map((option: string) => ({
          label: option,
          value: option
        }));
        return (
          <Checkbox
            key={key}
            label={item.label}
            inputName={fieldName}
            options={checkboxOptionsArray || []}
            register={register}
            required={item.required}
          />
        );
      }
      case 'Dropdown': {
        const dropdownOptionsArray = item.value?.map((option: string) => ({
          label: option,
          value: option
        }));
        return (
          <Select
            key={key}
            label={item.label}
            name={fieldName}
            options={dropdownOptionsArray || []}
            register={register}
            required={item.required}
          />
        );
      }
      default:
        return null;
    }
  };

  const handleNextStepCallback = () => {
    if (requestType.includes('Open House')) {
      return nextStepCallback && nextStepCallback(2);
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onFormSubmit: SubmitHandler<any> = async (data) => {
    const captchaToken = await captchaVerify();

    setDisabled(true);

    if (!captchaToken) {
      setErrorMessage('There was a problem with the captcha. Please try again.');
      setDisabled(false);
      return;
    }

    if (data.email === '' && data.phone === '') {
      setErrorMessage('Please enter an email or phone number.');
      setDisabled(false);
      return;
    } else {
      setErrorMessage('');
    }

    const optionalFieldsData = optionalFields?.items?.map((item: CMSField) => {
      const sanitizedLabel = toKebabCase(item.name || item.label).replace(/[^a-z0-9-]/g, '');
      const fieldName = item.label || item.name;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const value: string | string[] | undefined = (data as Record<string, any>)[sanitizedLabel];

      return {
        name: fieldName,
        value: Array.isArray(value) ? value.join(', ') : (value?.toString() ?? '')
      };
    });

    // Clean top-level data (remove the optional fields)
    const cleanedData = { ...data };
    optionalFields?.items?.forEach((item: CMSField) => {
      const fieldName = item.name || item.label;
      delete cleanedData[fieldName];
    });

    if (!shouldShowComments) {
      cleanedData.comments = '';
    }

    const getRequestType = (): string[] => {
      const requestTypeArray = Array.isArray(requestType) ? [...requestType] : [requestType || ''];

      const hasLeadsPlus = leadsPlusAgent?.agentId;

      if (hasLeadsPlus) {
        requestTypeArray.push('Leads Plus');
      }

      return requestTypeArray;
    };

    const requestTypeValue = getRequestType();

    const getEmailSubject = () => {
      if (emailSubject) {
        return emailSubject;
      }

      if (requestTypeValue.length > 0 && requestTypeValue[0] !== '') {
        return `${requestTypeValue[0]} Information Request`;
      }

      return 'Contact Form Submission';
    };

    const givenConsentValue = () => {
      if (shouldShowConsentToContact) {
        return data?.hasGivenConsent === 'true' ? true : false;
      } else {
        return null;
      }
    };

    const finalPayload = {
      ...cleanedData,
      optionalFields: optionalFieldsData || [],
      requestType: requestTypeValue,
      agentIds: getAgentIds(),
      listingId: listingId || null,
      officeId: officeId || null,
      extraRecipients: extraRecipients || [],
      recipientNotification: recipientNotification || '',
      confirmationToSender: confirmationToSender || '',
      emailSubject: getEmailSubject(),
      fromUrl: `${getSiteURL()}${router.asPath}`,
      userId: userInfo?.['https://www.sibcycline.com/user_metadata']?.user_id || null,
      hasGivenConsent: givenConsentValue(),
      captchaToken: captchaToken,
      contextData: contextData || {},
      workingWithAgent: data?.workingWithAgent || null
    };

    await axios({
      data: {
        ...finalPayload
      },
      method: 'POST',
      url: '/api/forms'
    })
      .then((res) => {
        if (res.status === 200) {
          setDisabled(false);
          handleNextStepCallback();

          setSubmitted(true);

          Toast.notify({
            message: 'We have received your request.',
            type: 'success'
          });
          setErrorMessage('');
        }
      })
      .catch((error) => {
        setDisabled(false);

        Toast.notify({
          message: `Failed to submit form.`,
          type: 'danger'
        });

        // Submit error report to rollbar on form failure
        reportError(error);

        setErrorMessage('There was an error submitting the form. Please try again.');
      });
  };

  const renderRichTextOrString = (content: string | RichTextField | undefined) => {
    if (!content) return null;
    if (typeof content === 'object' && 'props' in content) {
      return <>{content}</>;
    }
    if (typeof content === 'string') return <p>{content}</p>;
    if (content.json) return <Content content={content} />;
    return null;
  };

  return (
    <section className={classes}>
      {backgroundImage?.src && (
        <div className={form_bg_img}>
          <Image
            image={{
              ...backgroundImage
            }}
            backgroundImage
            layout="fill"
            objectFit="cover"
          />
        </div>
      )}
      <div className={form_container}>
        <div className={form_inside_container}>
          {submitted ? (
            <div className={form_success}>
              <div>Thank you for your interest.</div>
              <div>Your request has been received.</div>
            </div>
          ) : (
            <form
              className={form}
              onSubmit={(e) => {
                e.preventDefault();
                void handleSubmit(onFormSubmit)(e);
              }}
            >
              <>
                {header && (
                  <legend id={slugify(header)} className={form_title}>
                    {header}
                  </legend>
                )}
                {formDescription && renderRichTextOrString(formDescription)}

                {/* Default Fields */}
                <fieldset className={form_row} data-flex="row">
                  <Input
                    name="name"
                    label="Full Name"
                    required
                    register={register('name', {
                      required: 'Full name is required'
                    })}
                    defaultInputValue={fullName}
                  />
                </fieldset>
                <fieldset className={form_row}>
                  <Input
                    name="email"
                    label="Email"
                    register={register('email', {
                      pattern: {
                        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                        message: 'Please enter a valid email'
                      }
                    })}
                    defaultInputValue={userInfo?.email || ''}
                    type="email"
                  />
                  <Input
                    name="phone"
                    label="Phone"
                    register={register('phone', {
                      pattern: {
                        value: /^(\+\d{1,2}\s)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/,
                        message: 'Please enter a valid phone number'
                      }
                    })}
                    type="tel"
                  />
                </fieldset>
                {/* Optional Fields */}
                {optionalFields &&
                  optionalFields?.items?.map((item: CMSField, index: number) => {
                    return inputType(item, index);
                  })}
                {/* Comments field */}
                {shouldShowComments && (
                  <Textarea
                    defaultInputValue={defaultCommentValue || ''}
                    name="comments"
                    label="Message"
                    required={false}
                    register={register}
                    rows="4"
                  />
                )}
                {/* Are you working with an agent? */}
                {shouldShowWorkingWithAgent && (
                  <Input
                    defaultInputValue={workingWithAgentValue || null}
                    name="workingWithAgent"
                    label="Are you working with an agent?"
                    required={false}
                    register={register('workingWithAgent', {
                      required: false
                    })}
                    type="text"
                    placeholder="Agent Name, Broker"
                  />
                )}
                {/* Consent to contact */}
                {shouldShowConsentToContact && (
                  <Checkbox
                    inputName="hasGivenConsent"
                    options={[
                      { label: 'Yes, I consent to be contacted by an agent', value: 'true' }
                    ]}
                    register={register}
                  />
                )}

                {/* Submit Button */}
                <Button
                  className={submit_button}
                  label={submitLabel}
                  type="submit"
                  disabled={disabled}
                />

                {!!errorMessage && <p className={error_style}>{errorMessage}</p>}

                <div>
                  <p className={disclaimer}>
                    By providing a telephone number and submitting the form, you are consenting to
                    be contacted by SMS text message and agreeing to our{' '}
                    <a href="/privacy" target="_blank" rel="noopener noreferrer">
                      Privacy Policy.
                    </a>{' '}
                    Message frequency may vary. Message and data rates may apply. Reply STOP to opt
                    out out of further messaging. Reply HELP for more information.
                  </p>
                  <p className={disclaimer}>
                    This site is protected by reCAPTCHA and the Google{' '}
                    <a
                      href="https://policies.google.com/privacy"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Privacy Policy
                    </a>{' '}
                    and{' '}
                    <a
                      href="https://policies.google.com/terms"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Terms of Service
                    </a>{' '}
                    apply.
                  </p>
                </div>
              </>
            </form>
          )}
        </div>
        {(supportHeader || supportDescription) && (
          <div className={form_support_block}>
            <div>
              {supportImage && (
                <div className={form_image}>
                  <Image
                    image={{
                      ...supportImage
                    }}
                    objectFit="contain"
                  />
                </div>
              )}
              <div className={form_support_block_text}>
                <h2>{supportHeader}</h2>
                {supportDescription && <>{renderRichTextOrString(supportDescription)}</>}
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default DynamicForm;
